import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import '../map/cairn-map.js'
import type { PlanLocation } from '../../schemas/plan.js'

@customElement('trip-map-preview')
export class TripMapPreview extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      height: 120px;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      margin: 0;
    }

    .no-location {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      background: var(--cairn-surface-color, #f8f9fa);
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 14px;
    }

    cairn-map {
      width: 100%;
      height: 100%;
    }
  `

  /** Plan ID — loads route features automatically */
  @property({ type: String })
  planId?: string

  /**
   * Optional plan location — used when there is no saved plan yet (e.g. the
   * create-plan form). If set and `planId` is not, the preview map centers
   * on the location's bounds or coordinates.
   */
  @property({ type: Object })
  location?: PlanLocation

  private get locationBounds(): { sw: [number, number]; ne: [number, number] } | undefined {
    const loc = this.location
    if (!loc) return undefined
    if (loc.bounds) return loc.bounds
    if (loc.coordinates) {
      // Build a small ~2 km box around the point so the map zooms in nicely.
      const [lat, lng] = loc.coordinates
      const d = 0.01
      return { sw: [lat - d, lng - d], ne: [lat + d, lng + d] }
    }
    return undefined
  }

  private get hasData(): boolean {
    return !!this.planId || !!this.locationBounds
  }

  override render(): unknown {
    if (!this.hasData) {
      return html`<div class="no-location">No location data</div>`
    }

    return html`<cairn-map
      .planId=${this.planId}
      .initialBounds=${this.locationBounds}
      ?preview=${true}
    ></cairn-map>`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'trip-map-preview': TripMapPreview
  }
}
